import { useCallback } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import {
  ALL_PROVIDER_LANGUAGE_CONFIGS,
  STT_PROVIDER_LANGUAGE_MAP,
  resolveDeepgramLanguage,
} from '@/constants/voiceProviderLanguageMap';

/**
 * Manages language across all voice providers (STT + TTS).
 *
 * - Reads from the canonical `config.language` field (single source of truth)
 * - Writes to `config.language` + all 6 provider configs at once
 */
export const useProviderLanguage = () => {
  const { control, getValues, setValue } = useFormContext();

  const languageValue: string = useWatch({ control, name: 'config.language' }) ?? 'en';

  const setLanguage = useCallback(
    (lang: string) => {
      // Write to canonical field
      setValue('config.language', lang, { shouldDirty: true });
      // Deepgram's language is gated by its model (flux-general-en is English-only).
      const deepgramModel = getValues('config.stt_deepgram.model') as string | undefined;
      ALL_PROVIDER_LANGUAGE_CONFIGS.forEach((m) => {
        const value =
          m.fieldPath === STT_PROVIDER_LANGUAGE_MAP.deepgram.fieldPath
            ? resolveDeepgramLanguage(lang, deepgramModel)
            : m.toProvider(lang);
        setValue(m.fieldPath, value, { shouldDirty: true });
      });
    },
    [setValue, getValues]
  );

  return { languageValue, setLanguage };
};
